// src/components/detail-view.js
import React, { useState, useEffect, useRef } from 'react';
import './detail-view.css';
import { getApiBaseUrl, getItemById, getItemColor } from '../../config/oncorisk-config';
import OncoRiskTable from '../modules/oncorisk-table';
import { getCookieId } from '../../WebserverUser';
import { serviceRequest } from '../../../shared/xomics-webserver-client';

// Configuration for rendering related data as tables
// Maps a key in detailData to:
//   - tableId: The 'id' prop for OncoRiskTable (e.g., 'assertions', 'evidence')
//   - title: A user-friendly title for the section header
const RELATED_TABLE_CONFIG = {
  // For Features (as seen in the screenshot)
  'variants': { tableId: 'variants', title: 'Associated Variants' },
  'associated_diseases': { tableId: 'diseases', title: 'Associated Diseases' },
  'associated_therapies': { tableId: 'therapies', title: 'Associated Therapies' },
  'associated_assertions': { tableId: 'assertions', title: 'Associated Assertions' },
  
  // For Molecular Profiles
  '_assertions': { tableId: 'assertions', title: 'Related Assertions' },
  '_variants': { tableId: 'variants', title: 'Related Variants (from CIViC)' },
  'evidence_ids': { tableId: 'evidence', title: 'Evidence Items' },
  '_evidence_items': { tableId: 'evidence', title: 'Related Evidence Items (from CIViC)' },
  
  // For Diseases
  'linked_assertions': { tableId: 'assertions', title: 'Assertions for this Disease' },
  'associated_features': { tableId: 'features', title: 'Associated Features for this Disease' },

  // For Variants
  '_molecular_profiles': { tableId: 'molecularprofiles', title: 'Molecular Profiles Containing This Variant'},
  '_variant_groups': { tableId: 'variantgroups', title: 'Variant Groups Containing This Variant'},

  // Add other mappings here as needed for other item types...
};


function DetailView({ itemType, itemId, itemName, onBack, onNavigate, isLightTheme }) { 
  const [detailData, setDetailData] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [variantDetails, setVariantDetails] = useState({}); // Cache of variantId -> full detail
  const [clinvarDetails, setClinvarDetails] = useState({}); // Cache of clinvarId -> full clinvar detail
  const [clinvarLoading, setClinvarLoading] = useState({});
  const [selectedClinvarId, setSelectedClinvarId] = useState(null); // Currently selected ClinVar ID to display details
  const [oncokbDetails, setOncokbDetails] = useState({}); // Cache for GENE:ALTERATION -> cleaned data
  const [oncokbLoading, setOncokbLoading] = useState(false);
  const inFlightOncoKBRequests = useRef(new Set()); // (You already added this in Step 2)

  useEffect(() => {
    if (Object.keys(variantDetails).length === 0) return;

    const variantsToFetch = [];
    Object.values(variantDetails).forEach(variant => {
      if (!variant || variant.__error || !variant.entrez_name || !variant.name) return;

      const gene = variant.entrez_name.trim().toUpperCase();
      const alteration = variant.name.trim().toUpperCase();
      const canonicalId = `${gene}:${alteration}`;

      // CRITICAL FIX: Do NOT fetch if we have data OR if a request is already in-flight for it.
      if (!oncokbDetails[canonicalId] && !inFlightOncoKBRequests.current.has(canonicalId)) {
        variantsToFetch.push({ gene, alteration });
        // Mark as "in-flight" immediately so we don't ask again.
        inFlightOncoKBRequests.current.add(canonicalId);
      }
    });

    if (variantsToFetch.length === 0) return;

    setOncokbLoading(true);

    serviceRequest('oncorisk', getCookieId(), '/oncokb', {
      method: 'POST',
      body: JSON.stringify({ variants: variantsToFetch })
    })
    .then(data => {
      setOncokbDetails(prev => ({ ...prev, ...data }));
    })
    .catch(error => {
      console.error(`Error fetching OncoKB data:`, error);
      const errorData = {};
      variantsToFetch.forEach(({ gene, alteration }) => {
        errorData[`${gene}:${alteration}`] = { error: error.message };
      });
      setOncokbDetails(prev => ({ ...prev, ...errorData }));
    })
    .finally(() => {
      // Clean up: Remove these IDs from the "in-flight" tracker.
      variantsToFetch.forEach(({ gene, alteration }) => {
        const canonicalId = `${gene.toUpperCase()}:${alteration.toUpperCase()}`;
        inFlightOncoKBRequests.current.delete(canonicalId);
      });
      setOncokbLoading(false);
    });

  }, [variantDetails, oncokbDetails]); // Dependency on oncokbDetails is safe with this ref pattern


  
  const getTypeIcon = (type) => {
    const cfg = getItemById(type.endsWith('s') ? type : `${type}s`);
    return cfg?.icon ? `fa-${cfg.icon}` : 'fa-info-circle';
  };
  
  const getTypeColor = (type) => {
    // Get normalized type name (ensure singular form ends with 's' for config lookup)
    const normalizedType = type.endsWith('s') ? type : `${type}s`;
    // Strip 's' to get the key for itemColors, handling special cases like 'therapies' -> 'therapy'
    let colorType;
    if (normalizedType.endsWith('ies')) {
      colorType = normalizedType.slice(0, -3) + 'y'; // 'therapies' -> 'therapy'
    } else if (normalizedType.endsWith('s')) {
      colorType = normalizedType.slice(0, -1); // 'variants' -> 'variant'
    } else {
      colorType = normalizedType;
    }
    return getItemColor(colorType);
  };
  
  const createGradientStyle = (color) => {
    // Parse rgba to get components
    const rgbaMatch = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
    if (!rgbaMatch) return { background: color };
    
    const [_, r, g, b, a] = rgbaMatch;
    // Create gradient from full color to low opacity
    return {
      background: `linear-gradient(135deg, rgba(${r},${g},${b},${a}), rgba(${r},${g},${b},0.1))`
    };
  };

  useEffect(() => {
    if (!itemId) return;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${getApiBaseUrl()}/items/${itemType}/${itemId}`);
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const data = await res.json();
        setDetailData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [itemType, itemId]);

  // Fetch full details for any associated variants (if not already cached)
  useEffect(() => {
    if (!detailData) return;
    const variantsArray = Array.isArray(detailData._variants) ? detailData._variants : (Array.isArray(detailData.variants) ? detailData.variants : []);
    const idsToFetch = variantsArray
      .map(v => v && v.id)
      .filter(id => id != null && !variantDetails[id]);

    if (idsToFetch.length === 0) return;

    Promise.all(
      idsToFetch.map(id =>
        fetch(`${getApiBaseUrl()}/items/variants/${id}`)
          .then(res => {
            if (!res.ok) throw new Error(`Failed ${res.status}`);
            return res.json();
          })
          .then(data => ({ id, data }))
          .catch(err => ({ id, error: err.message }))
      )
    ).then(results => {
      setVariantDetails(prev => {
        const updated = { ...prev };
        results.forEach(({ id, data, error }) => {
          updated[id] = error ? { __error: error } : data;
        });
        return updated;
      });
    });
  }, [detailData]);
  
  // Fetch ClinVar data for ClinVar IDs
  useEffect(() => {
    // First check if we have variants with full details
    if (!detailData) return;
    const variantsArray = Array.isArray(detailData._variants) ? detailData._variants : (Array.isArray(detailData.variants) ? detailData.variants : []);
    if (variantsArray.length === 0) return;
    
    // Get all ClinVar IDs from all variants with full details
    const clinvarIdsToFetch = [];
    
    // Find all ClinVar IDs across variants that aren't already fetched
    Object.entries(variantDetails).forEach(([variantId, variantData]) => {
      if (!variantData || variantData.__error) return;
      
      const clinvarEntries = variantData.clinvar_entries;
      if (!clinvarEntries || clinvarEntries.length === 0) return;
      
      // Add ClinVar IDs that we haven't fetched yet
      clinvarEntries.forEach(clinvarId => {
        if (!clinvarDetails[clinvarId] && !clinvarLoading[clinvarId]) {
          clinvarIdsToFetch.push(clinvarId);
          // Mark as loading to prevent duplicate fetches
          setClinvarLoading(prev => ({ ...prev, [clinvarId]: true }));
        }
      });
    });
    
    // If no new ClinVar IDs to fetch, return
    if (clinvarIdsToFetch.length === 0) return;
    
    // Batch fetch ClinVar data for all IDs in one request
// src/components/detail-view.js

    // Batch fetch ClinVar data for all IDs in one request
    serviceRequest('oncorisk', getCookieId(), '/clinvar', {
      method: 'POST',
      body: JSON.stringify({ ids: clinvarIdsToFetch })
    })
    .then(data => {
      // The `data` is the real object from the server.
      // We use it directly to update our state.
      setClinvarDetails(prev => ({
        ...prev,
        ...data 
      }));
    })
    .catch(error => {
      console.error(`Error fetching ClinVar data:`, error);
      // Mark all requested IDs with an error if the fetch fails
      const errorData = {};
      clinvarIdsToFetch.forEach(id => {
        errorData[id] = { error: error.message }; // Use .error to match NCBI's format
      });
      setClinvarDetails(prev => ({
        ...prev,
        ...errorData
      }));
    })
    .finally(() => {
      const loadingUpdates = {};
      clinvarIdsToFetch.forEach(id => {
        loadingUpdates[id] = false;
      });
      setClinvarLoading(prev => ({ ...prev, ...loadingUpdates }));
    });
  }, [detailData, variantDetails]);

  // Helper to extract IDs if the value is an array of objects [{id: ...}, ...]
  const extractIds = (value) => {
    if (Array.isArray(value)) {
      return value.map(item => typeof item === 'object' && item !== null ? item.id : item)
        .filter(Boolean);
    }
    return typeof value === 'object' && value !== null ? [value.id].filter(Boolean) : [];
  };
  
  const getClinSigClass = (significance) => {
    if (!significance) return '';
    const sigLower = significance.toLowerCase();
    
    if (sigLower.includes('pathogenic') && !sigLower.includes('not') && !sigLower.includes('likely')) {
      return 'OCRP-clinvar-pathogenic';
    } 
    else if (sigLower.includes('likely pathogenic')) {
      return 'OCRP-clinvar-pathogenic';
    }
    else if (sigLower.includes('benign') && !sigLower.includes('not') && !sigLower.includes('likely')) {
      return 'OCRP-clinvar-benign';
    }
    else if (sigLower.includes('likely benign')) {
      return 'OCRP-clinvar-benign';
    }
    else if (sigLower.includes('uncertain') || sigLower.includes('unknown') || sigLower.includes('not provided')) {
      return 'OCRP-clinvar-uncertain';
    }
    else if (sigLower.includes('conflict')) {
      return 'OCRP-clinvar-conflict';
    }
    
    return 'OCRP-clinvar-uncertain';
  };

  const formatPropertyName = (key) => {
    return key.replace(/^_/, '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const handleExportJSON = () => {
    if (!detailData) return;

    // Combine all data including separately fetched enrichments
    const exportData = {
      ...detailData,
      _enrichedData: {
        variantDetails: Object.keys(variantDetails).length > 0 ? variantDetails : undefined,
        clinvarDetails: Object.keys(clinvarDetails).length > 0 ? clinvarDetails : undefined,
        oncokbDetails: Object.keys(oncokbDetails).length > 0 ? oncokbDetails : undefined,
      }
    };

    // Remove _enrichedData if empty
    if (!exportData._enrichedData.variantDetails &&
        !exportData._enrichedData.clinvarDetails &&
        !exportData._enrichedData.oncokbDetails) {
      delete exportData._enrichedData;
    }

    const dataStr = JSON.stringify(exportData, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${itemType}_${itemId}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="OCRP-detail-view">
      <div className="OCRP-detail-back" onClick={onBack}>
        <i className="fas fa-arrow-left"></i>
      </div>

      <div className="OCRP-detail-header">
        <div className="OCRP-detail-icon" style={createGradientStyle(getTypeColor(itemType))}>
          <i className={`fas ${getTypeIcon(itemType)}`}></i>
        </div>
        <div className="OCRP-detail-info">
          {(() => {
            // This logic determines the PRIMARY and SECONDARY entities for the header.

            let primaryType, primaryId, primaryName;
            let secondaryType, secondaryId, secondaryName;

            // --- CASE 1: We are on a page that is NOT a feature, but HAS a feature ---
            // Example: Molecular Profile page. Primary=MP, Secondary=Feature/Gene
            if (itemType !== 'features' && detailData?.feature_id?.id) {
              primaryType = itemType; // 'molecularprofiles'
              primaryId = detailData.id;
              primaryName = detailData.name;

              secondaryType = 'features';
              secondaryId = detailData.feature_id.id;
              secondaryName = detailData.feature_id.name;
            }
            // --- CASE 2: We are on a page that HAS an associated variant ---
            // Example: Feature page that has variants. Primary=Feature, Secondary=First Variant
            else {
              const variantsArray = Array.isArray(detailData?._variants) ? detailData._variants : (Array.isArray(detailData?.variants) ? detailData.variants : []);
              const firstVariant = variantsArray.length > 0 ? variantsArray[0] : null;
              const firstFull = firstVariant ? variantDetails[firstVariant.id] : null;

              if (itemType === 'features' && firstFull && !firstFull.__error) {
                primaryType = 'features';
                primaryId = detailData.id;
                primaryName = detailData.name;

                secondaryType = 'variants';
                secondaryId = firstFull.id;
                secondaryName = firstFull.name;
              }
              // --- FALLBACK CASE: Generic page (e.g., Disease page) ---
              // Primary is just the item itself. No secondary link.
              else {
                primaryType = itemType;
                primaryId = detailData?.id || itemId;
                primaryName = detailData?.name || itemName;
              }
            }

            // --- Now, render the header based on what we found ---
            return (
              <div className="OCRP-detail-info-press">
                {primaryName && (
                  <div
                    className="OCRP-variant-name-header" // Re-using style, it's like a main title
                    onClick={() => onNavigate(primaryType, primaryId, primaryName)}
                    style={{ cursor: 'pointer' }}
                    title={`View ${formatPropertyName(primaryType)}: ${primaryName}`}
                  >
                    {primaryName}
                  </div>
                )}
                {secondaryName && (
                  <div
                    className="OCRP-variant-gene-header" // Re-using style, it's like a subtitle
                    onClick={() => onNavigate(secondaryType, secondaryId, secondaryName)}
                    style={{ cursor: 'pointer' }}
                    title={`View ${formatPropertyName(secondaryType)}: ${secondaryName}`}
                  >
                    {secondaryName}
                  </div>
                )}
                {/* Show the plain type label only if there is no secondary link */}
                {!secondaryName && (
                   <p className="OCRP-detail-type">
                     {getItemById(itemType)?.label || formatPropertyName(itemType)}
                   </p>
                )}
              </div>
            );
          })()}
        </div>
        {detailData?.aliases && Array.isArray(detailData.aliases) && detailData.aliases.length > 0 && (
          <div className="OCRP-detail-aliases">
            {detailData.aliases.map((alias, index) => (
              <div key={index} className="OCRP-alias-chip" style={{backgroundColor: `${getTypeColor(itemType)}33`}}>
                {alias}
              </div>
            ))}
          </div>
        )}
        {detailData && (
          <div className="OCRP-detail-export-row">
            <button className="OCRP-detail-export-btn" onClick={handleExportJSON}>
              <i className="fas fa-download"></i> Export Data
            </button>
          </div>
        )}
      </div>

      <div className="OCRP-detail-content">
        {loading ? (
          <div className="OCRP-detail-loading">
            <div className="OCRP-detail-loading-spinner"></div>
            <p>Loading {formatPropertyName(itemType)} details…</p>
          </div>
        ) : error ? (
          <div className="OCRP-detail-error">
            <i className="fas fa-exclamation-circle"></i>
            <p>Error loading data: {error}</p>
          </div>
        ) : detailData ? (
          <div className="OCRP-detail-data">
 

            {(() => {
              const variantsArray = Array.isArray(detailData._variants) ? detailData._variants : (Array.isArray(detailData.variants) ? detailData.variants : []);
              const hasVariants = Array.isArray(variantsArray) && variantsArray.length > 0;
              
              // If no variants or no variant data loaded yet, hide this section
              if (!hasVariants) return null;
              
              // Check if we have at least one variant with details loaded
              const firstVariant = variantsArray[0];
              
              // Safety check: if firstVariant is null/undefined, hide this section
              if (!firstVariant || !firstVariant.id) return null;
              
              const firstFull = firstVariant && variantDetails[firstVariant.id];
              const isLoading = firstVariant && variantDetails[firstVariant.id] === undefined;
              
              // If still loading the first variant, show loading indicator
              if (isLoading) {
                return (
                  <div className="OCRP-variant-loading">
                    <div className="OCRP-detail-loading-spinner"></div>
                    <p>Loading variant information...</p>
                  </div>
                );
              }
              
              // If we have no full variant data yet, hide section
              if (!firstFull || firstFull.__error) return null;
              
              // Extract name and gene for header
              const variantName = firstFull.name;
              const geneName = firstFull.entrez_name;
              
              // Summary data tiles
              const summaryWanted = ['hgvs_expressions', 'variant_aliases', 'allele_registry_id'];
              const summaryData = Object.fromEntries(
                summaryWanted
                  .filter(k => firstFull[k] != null)
                  .map(k => [k, firstFull[k]])
              );
              
              return (
                <>
                  {/* Variant Name and Gene Header */}
              
                  
     


                  <div className="OCRP-detail-section-top">
                    {/* --- ClinVar Information Panel --- */}
                    <div className="OCRP-detail-section-top-body OCRP-clinvar-panel">
                      <h3>ClinVar Information</h3>
                      {variantsArray.map((variant) => {
                        const fullVariant = variantDetails[variant.id];
                        if (fullVariant === undefined) return <p key={variant.id}>Loading variant details…</p>;
                        if (fullVariant.__error) return <p key={variant.id} className="OCRP-error">Error: {fullVariant.__error}</p>;
                        if (!fullVariant.clinvar_entries || fullVariant.clinvar_entries.length === 0) return null;

                        return (
                          <div key={variant.id} className="OCRP-variant-clinvar-group">
                            {fullVariant.clinvar_entries.map(clinvarId => {
                              const clinvarRecord = clinvarDetails[clinvarId];
                              const isClinvarLoading = clinvarLoading[clinvarId] || !clinvarRecord;

                              if (isClinvarLoading) {
                                return (
                                  <div key={clinvarId} className="OCRP-clinvar-card-loading">
                                    <div className="OCRP-detail-loading-spinner-small"></div>
                                    <p>Loading ClinVar Record: {clinvarId}...</p>
                                  </div>
                                );
                              }

                              if (clinvarRecord.error) {
                                return null; // Hide error records completely
                              }

                              const grch38_loc = clinvarRecord.variation_set?.[0]?.variation_loc?.find(loc => loc.assembly_name === 'GRCh38');
                              const dbSnpXref = clinvarRecord.variation_set?.[0]?.variation_xrefs?.find(ref => ref.db_source === 'dbSNP');

                              return (
                                <div key={clinvarId} className="OCRP-clinvar-card">
                                  <div className="OCRP-clinvar-card-header">
                                    <div className="OCRP-clinvar-card-title-block">
                                      <h5>{clinvarRecord.genes?.[0]?.symbol}: {clinvarRecord.title}</h5>
                                      <p className="OCRP-clinvar-card-subtitle">ClinVar Accession: {clinvarRecord.accession}</p>
                                    </div>
                                    <a href={`https://www.ncbi.nlm.nih.gov/clinvar/variation/${clinvarId}/`} target="_blank" rel="noopener noreferrer" className="OCRP-clinvar-details-link">
                                      NCBI <i className="fas fa-external-link-alt"></i>
                                    </a>
                                  </div>

                                  <div className="OCRP-clinvar-card-content-grid">
                                    {/* --- LEFT COLUMN: INTERPRETATIONS --- */}
                                    <div className="OCRP-clinvar-column">
                                      {clinvarRecord.germline_classification && (
                                        <div className="OCRP-clinvar-interp-block">
                                          <label>Germline Classification</label>
                                          <div className={`OCRP-clinvar-interp-title ${getClinSigClass(clinvarRecord.germline_classification.description)}`}>
                                            {clinvarRecord.germline_classification.description}
                                          </div>
                                          <div className="OCRP-clinvar-interp-meta">{clinvarRecord.germline_classification.review_status} (Eval: {new Date(clinvarRecord.germline_classification.last_evaluated).toLocaleDateString()})</div>
                                          <div className="OCRP-clinvar-value-tags">
                                            {clinvarRecord.germline_classification.trait_set.map(t => t.trait_name).filter(n => n.toLowerCase() !== 'not provided').map((name, i) => <span key={i} className="OCRP-clinvar-tag">{name}</span>)}
                                          </div>
                                        </div>
                                      )}
                                      {clinvarRecord.clinical_impact_classification && (
                                        <div className="OCRP-clinvar-interp-block">
                                          <label>Somatic Clinical Impact</label>
                                          <div className="OCRP-clinvar-interp-title OCRP-clinvar-somatic">
                                            {clinvarRecord.clinical_impact_classification.description}
                                          </div>
                                          <div className="OCRP-clinvar-interp-meta">{clinvarRecord.clinical_impact_classification.review_status} (Eval: {new Date(clinvarRecord.clinical_impact_classification.last_evaluated).toLocaleDateString()})</div>
                                           <div className="OCRP-clinvar-value-tags">
                                            {clinvarRecord.clinical_impact_classification.trait_set.map(t => t.trait_name).map((name, i) => <span key={i} className="OCRP-clinvar-tag">{name}</span>)}
                                          </div>
                                        </div>
                                      )}
                                    </div>

                                    {/* --- RIGHT COLUMN: VARIANT DETAILS --- */}
                                    <div className="OCRP-clinvar-column">
                                      <div className="OCRP-clinvar-detail-group">
                                        <div className="OCRP-clinvar-detail-item">
                                          <label>Variant Type</label>
                                          <div className="OCRP-clinvar-value">{clinvarRecord.variant_type}</div>
                                        </div>
                                        {dbSnpXref && <div className="OCRP-clinvar-detail-item">
                                          <label>dbSNP ID</label>
                                          <div className="OCRP-clinvar-value">{dbSnpXref.db_id}</div>
                                        </div>}
                                        {grch38_loc && <div className="OCRP-clinvar-detail-item">
                                          <label>Location (GRCh38)</label>
                                          <div className="OCRP-clinvar-value">chr{grch38_loc.chr}:{grch38_loc.start}</div>
                                        </div>}
                                      </div>
                                      {clinvarRecord.molecular_consequence_list?.length > 0 && (
                                        <div className="OCRP-clinvar-detail-item">
                                          <label>Molecular Consequences</label>
                                          <div className="OCRP-clinvar-value-tags">
                                            {clinvarRecord.molecular_consequence_list.map((c, i) => <span key={i} className="OCRP-clinvar-tag-grey">{c}</span>)}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>

                    {/* --- OncoKB Panel (STYLED TILES) --- */}
                    <div className="OCRP-detail-section-top-body OCRP-oncokb-panel">
                      <h3>OncoKB Clinical Significance</h3>
                      {oncokbLoading && !Object.keys(oncokbDetails).length > 0 && (
                          <div className="OCRP-clinvar-card-loading">
                              <div className="OCRP-detail-loading-spinner-small"></div>
                              <p>Loading OncoKB data...</p>
                          </div>
                      )}
                      
                      {variantsArray.map((variant) => {
                        const full = variantDetails[variant.id];
                        if (!full || full.__error || !full.entrez_name || !full.name) {
                          return null;
                        }

                        const canonicalId = `${full.entrez_name.toUpperCase()}:${full.name.toUpperCase()}`;
                        const oncokbData = oncokbDetails[canonicalId];

                        if (!oncokbData) {
                          return (
                              <div key={variant.id} className="OCRP-oncokb-placeholder">
                                  <div className="OCRP-detail-loading-spinner-small"></div>
                                  <span>Fetching data for {full.entrez_name} {full.name}...</span>
                              </div>
                          );
                        }

                        if (oncokbData.error) {
                          return (
                            <div key={variant.id} className="OCRP-oncokb-card OCRP-oncokb-error">
                              <div className="OCRP-oncokb-header">
                                <h5>OncoKB Data: {full.entrez_name} {full.name}</h5>
                                <p className="OCRP-error">Error: {oncokbData.error}</p>
                              </div>
                            </div>
                          );
                        }

                        return (
                          <div key={variant.id} className="OCRP-oncokb-card">
                            <div className="OCRP-oncokb-header">
                              <div className="OCRP-oncokb-title-block">
                                <h5>{oncokbData.summary?.gene}: {oncokbData.summary?.alteration}</h5>
                                <p className="OCRP-oncokb-subtitle">Oncogenicity: {oncokbData.summary?.oncogenicity}</p>
                              </div>
                              <a href={`https://www.oncokb.org/gene/${oncokbData.summary?.gene}/${oncokbData.summary?.alteration}`} target="_blank" rel="noopener noreferrer" className="OCRP-oncokb-details-link">
                                OncoKB <i className="fas fa-external-link-alt"></i>
                              </a>
                            </div>

                            <div className="OCRP-oncokb-content-grid">
                              {/* --- LEFT COLUMN: SUMMARY --- */}
                              <div className="OCRP-oncokb-column">
                                {oncokbData.summary && (
                                  <div className="OCRP-oncokb-summary-block">
                                    <label>Mutation Effect</label>
                                    <div className="OCRP-oncokb-effect">{oncokbData.summary.mutationEffect}</div>
                                    
                                    <label>Gene Summary</label>
                                    <div className="OCRP-oncokb-description">{oncokbData.summary.geneSummary}</div>
                                    
                                    <label>Variant Summary</label>
                                    <div className="OCRP-oncokb-description">{oncokbData.summary.variantSummary}</div>
                                  </div>
                                )}
                              </div>

                              {/* --- RIGHT COLUMN: EVIDENCE LEVELS --- */}
                              <div className="OCRP-oncokb-column">
                                {oncokbData.keyEvidenceLevels && (
                                  <div className="OCRP-oncokb-evidence-block">
                                    <label>Evidence Levels</label>
                                    <div className="OCRP-evidence-tags">
                                      {oncokbData.keyEvidenceLevels.highestSensitivity && oncokbData.keyEvidenceLevels.highestSensitivity !== 'None' && (
                                        <span className="OCRP-evidence-tag OCRP-sensitivity">
                                          Sensitivity: {oncokbData.keyEvidenceLevels.highestSensitivity}
                                        </span>
                                      )}
                                      {oncokbData.keyEvidenceLevels.highestFdaRecognition && oncokbData.keyEvidenceLevels.highestFdaRecognition !== 'None' && (
                                        <span className="OCRP-evidence-tag OCRP-fda">
                                          FDA: {oncokbData.keyEvidenceLevels.highestFdaRecognition}
                                        </span>
                                      )}
                                      {oncokbData.keyEvidenceLevels.highestResistance && oncokbData.keyEvidenceLevels.highestResistance !== 'None' && (
                                        <span className="OCRP-evidence-tag OCRP-resistance">
                                          Resistance: {oncokbData.keyEvidenceLevels.highestResistance}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* --- TREATMENTS SECTION --- */}
                            {oncokbData.topTierTreatments && oncokbData.topTierTreatments.length > 0 && (
                              <div className="OCRP-oncokb-treatments">
                                <label>Top Tier Treatments</label>
                                <div className="OCRP-treatments-grid">
                                  {oncokbData.topTierTreatments.slice(0, 6).map((treatment, idx) => (
                                    <div key={idx} className="OCRP-treatment-card">
                                      <div className="OCRP-treatment-drugs">{treatment.drugs}</div>
                                      <div className="OCRP-treatment-cancer">{treatment.cancerType}</div>
                                      <div className="OCRP-treatment-level">{treatment.evidenceLevel}</div>
                                     {/* {treatment.fdaApproval && (
                                        <div className="OCRP-treatment-fda">FDA Approved</div>
                                      )} */}
                                    </div>
                                  ))}
                                </div>
                                {oncokbData.topTierTreatments.length > 6 && (
                                  <div className="OCRP-treatments-more">
                                    +{oncokbData.topTierTreatments.length - 6} more treatments available
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              );
            })()}

            <div className="OCRP-detail-section">
              <h2>Overview</h2>
              <div className="OCRP-detail-properties">
                {detailData?.description && (
                  <div className="OCRP-detail-property-full-width">
                    <div className="OCRP-detail-property-value">
                      {detailData.description}
                    </div>
                  </div>
                )}
              </div>
            </div>
    

            {/* Dynamically Rendered Related Tables */}
            {(() => {
              // Track which table types we've already rendered to avoid duplicates
              const renderedTableTypes = new Set();
              
              return Object.entries(RELATED_TABLE_CONFIG).map(([dataKey, config]) => {
                const relatedItemsArray = detailData[dataKey];
                const itemIdsToFilter = extractIds(relatedItemsArray);
                
                // Skip this entry if we don't have IDs to filter by
                if (!itemIdsToFilter || itemIdsToFilter.length === 0) return null;
                
                // Skip if we've already rendered a table of this type
                if (renderedTableTypes.has(config.tableId)) return null;
                
                // Mark this table type as rendered
                renderedTableTypes.add(config.tableId);
                
                return (
                  <div className="OCRP-detail-section" key={dataKey}>
                    <h2>{config.title || `Related ${formatPropertyName(config.tableId)}`}</h2>
                    <OncoRiskTable
                      id={config.tableId} // e.g., 'assertions', 'evidence'
                      customTitle={config.title || `${itemIdsToFilter.length} Related ${formatPropertyName(config.tableId)}`}
                      onNavigate={onNavigate} // Pass navigation handler
                      filters={{ id: itemIdsToFilter }} // Filter by the extracted IDs
                      showActions={false}
                      isLightTheme={isLightTheme}
                    />
                  </div>
                );
              });
            })()}
                <div className="OCRP-detail-section">
              <h2>Additional Details</h2>
              <div className="OCRP-detail-properties">
                {Object.entries(detailData)
                  .filter(([key, value]) => {
                    // Skip if it's null
                    if (value === null) return false;

                    // Skip if it was shown in Overview (simple, non-array, non-object, not starting with '_')
                    const isOverviewProperty = typeof value !== 'object' && !key.startsWith('_') && !Array.isArray(value);
                    if (isOverviewProperty) return false;

                    // Skip if it was rendered as a table
                    const tableCfg = RELATED_TABLE_CONFIG[key];
                    if (tableCfg) {
                      const idsForTable = extractIds(value);
                      if (idsForTable && idsForTable.length > 0) return false;
                    }
                    // If we reach here, it's not in Overview and not rendered as a table with items.
                    return true;
                  })
                  .map(([key, value]) => (
                    <div className="OCRP-detail-property" key={key}>
                      <div className="OCRP-detail-property-name">
                        {formatPropertyName(key)}
                      </div>
                      <div className="OCRP-detail-property-value">
                        {Array.isArray(value)
                          ? value.length === 0 ? '(empty list)' :
                            typeof value[0] === 'object' ? `${value.length} objects (see Raw Data)`
                            : `${value.length} items: ${String(value.slice(0,3)).substring(0,50)}... (see Raw Data)`
                          : typeof value === 'object'
                            ? value.name || `Object (see Raw Data)` // Display name if it's a single resolved FK object
                            : String(value).length > 100 ? String(value).substring(0,97) + "..." : String(value)
                        }
                      </div>
                    </div>
                  ))}
              </div>
            </div>

          </div>
        ) : (
          <div className="OCRP-detail-empty">
            <i className="fas fa-info-circle"></i>
            <p>No data available for this {formatPropertyName(itemType)}.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default DetailView;
